import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, IconButton
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { automationService } from '../../services/automationService';

const AutomationList = () => {
  const { project } = useParams();
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const data = await automationService.getAutomations(project);
      setAutomations(data);
    } catch (err) {
      console.error('Error fetching automations:', err);
      setError('Failed to load automations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      fetchAutomations();
    }
  }, [project]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this automation?')) return;

    try {
      await automationService.deleteAutomation(id);
      setAutomations(prev => prev.filter(a => a._id !== id));
    } catch (err) {
      console.error('Error deleting automation:', err);
      alert('Failed to delete automation');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Automation Workflows
        </Typography>
        <Button
          variant="contained"
          color="info"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/project/${project}/automation/new`)}
        >
          Add New Template
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Created Date</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {automations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">No automations found</TableCell>
              </TableRow>
            ) : (
              automations.map((automation) => (
                <TableRow
                  key={automation._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/project/${project}/automation/${automation._id}`)}
                >
                  <TableCell color="primary.main">{automation.name}</TableCell>
                  <TableCell>{automation.status}</TableCell>
                  <TableCell>{new Date(automation.created_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(automation._id, e)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AutomationList;
